/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import PropTypes from 'prop-types' // Npm: react.js library.
import JoiBrowser from 'joi-browser' // Npm: Joi for frontend validation.
import _ from 'underscore' // Npm: Underscore.js library.
import { toast } from 'react-hot-toast' // Npm: React hot toast.
import { connect } from 'react-redux' // Npm: React Redux for state management.
import { useLazyQuery, useMutation } from '@apollo/client' // Npm: Apollo client.
import { HiMinusCircle, HiPlusCircle } from 'react-icons/hi' // Npm: React icons.
import { HiBolt } from 'react-icons/hi2' // Npm: React icons.
import {
  Button,
  Checkbox,
  Flex,
  Text,
  useBreakpointValue
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import SubmitButton from 'components/SubmitButton'
import MccMncSelector from 'components/MccMncSelector'
import VendorAccountSelector from 'components/VendorAccountSelector'
import { MemoizedInput } from 'components/MemoizedInput'


/*
 * GRAPHS
 */
import LBRRoutePlanReadQuery from './__query__/index.lbrroutePlan.read.query'
import LBRRoutePlanUpdateMutation from './__mutation__/index.lbrroutePlan.update.mutation'
import LBRRoutePlanCreateMutation from './__mutation__/index.lbrroutePlan.create.mutation'


/*
 * OBJECTS
 */
const Index = ({
  isOpen,
  isCreateOnly,
  onClose,
  passOn,
  refetch
}) => {
  // Hook assignment.
  const [isLoadBalancingOn, setIsLoadBalancingOn] = React.useState(true)
  const [forms, setForms] = React.useState([1])
  const [error, setError] = React.useState('')
  const [forceReRender, setForceReRender] = React.useState('')
  const [QueryLBRRoutePlanRead, QueryLBRRoutePlanReadResponse] = useLazyQuery(LBRRoutePlanReadQuery, { 'variables': { 'routePlanId': passOn?.routePlanId } })
  const [MutationLBRRoutePlanCreate, MutationLBRRoutePlanCreateResponse] = useMutation(LBRRoutePlanCreateMutation)
  const [MutationLBRRoutePlanUpdate, MutationLBRRoutePlanUpdateResponse] = useMutation(LBRRoutePlanUpdateMutation)
  const _formDataRef = React.useRef({})
  const _loadBalancingRef = React.useRef({})
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  const _SubmitForm = async e => {
    // Local variable.
    let _routePlanIds

    // Const assignment.
    const _loadBalancerFilteredData = []

    // Prevent default behavior.
    e.preventDefault()

    // Reset error.
    setError('')

    /*
     * If _loadBalancingRef is not empty then
     * validate it first.
     */
    if (isLoadBalancingOn) {
      // Local variable.
      let _loadCounter

      // Variable assignment.
      _loadCounter = 0

      // Const assignment.
      const _alreadyPushedData = []

      // If form is empty then report failure.
      if (_.isEmpty(_loadBalancingRef.current)) return setError('LoadBalancing: Please fill out the load balancing form.')

      // Loop over _loadBalancing and report failure if any key is empty.
      for (const [key, value] of Object.entries(_loadBalancingRef.current)) {
        // Const assignment.
        const _keySplit = key.split?.('__')

        // If key is empty then report failure.
        if (key.includes('vendorAccountId') && !_.isNumber(_loadBalancingRef?.current?.[`percentage__${_keySplit?.[1]}`])) return setError(`LoadBalancing: Field percentage__${_keySplit?.[1]} at position ${_keySplit?.[1]} is empty.`)
        if (key.includes('percentage') && _.isEmpty(_loadBalancingRef?.current?.[`vendorAccountId__${_keySplit?.[1]}`])) return setError(`LoadBalancing: Field vendorAccountId__${_keySplit?.[1]} at position ${_keySplit?.[1]} is empty.`)
        if (key.includes('percentage') && 0 > value) return setError(`LoadBalancing: Field percentage__${_keySplit?.[1]} at position ${_keySplit?.[1]} should be greater than 0.`)

        // Increase load counter.
        if (key.includes('percentage')) _loadCounter += value

        // Only push data if it is not already pushed.
        if (!_alreadyPushedData.includes(_keySplit?.[1])) {
          // Push data to _loadBalancerFilteredData.
          _loadBalancerFilteredData.push({ 'vendorAccountId': _loadBalancingRef?.current?.[`vendorAccountId__${_keySplit?.[1]}`]?.split?.(' ')?.[1]?.replace?.('(', '').replace?.(')', ''), 'percentage': _loadBalancingRef?.current?.[`percentage__${_keySplit?.[1]}`], 'vendorDisplayName': _loadBalancingRef?.current?.[`vendorAccountId__${_keySplit?.[1]}`]?.split?.(' ')?.[0] })

          // Push data to _alreadyPushedData.
          _alreadyPushedData.push(_keySplit?.[1])
        }
      }

      // Report failure if load counter is not 100.
      if (100 !== _loadCounter) return setError('LoadBalancing: Sum of all percentages should be 100.')

      // Validate loadBalancing.
      const _JoiSchemaLoadBalancing = JoiBrowser.array().items(JoiBrowser.object({
        'vendorAccountId': JoiBrowser.string().required(),
        'percentage': JoiBrowser.number().required(),
        'vendorDisplayName': JoiBrowser.string().required()
      }))

      // Validate loadBalancing.
      const _JoiSchemaLoadBalancingValidate = _JoiSchemaLoadBalancing.validate(_loadBalancerFilteredData)

      // If error exists then report failure.
      if (_JoiSchemaLoadBalancingValidate.error) return setError(`LoadBalancing: ${_JoiSchemaLoadBalancingValidate.error?.message}`)
    }

    // Const assignment.
    const _JoiSchema = JoiBrowser.object({
      'mcc': JoiBrowser.string().required(),
      'mnc': JoiBrowser.array().items(JoiBrowser.string().required()).required()
    }).options({ 'allowUnknown': true })

    // Make sure that mnc is array.
    if (!isCreateOnly && !_.isArray(_formDataRef.current?.mnc)) _formDataRef.current = { ..._formDataRef.current, 'mnc': [(_formDataRef.current?.mnc)?.split('(')[1]?.split(')')[0]] }

    // Remove all keys from _formDataRef.current which are undefined.
    _formDataRef.current = { ..._.pick(_formDataRef.current, _.identity), 'loadBalancerConfiguration': _formDataRef.current?.loadBalancerConfiguration ?? [] }

    // Validate form data.
    const _JoiSchemaValidate = _JoiSchema.validate(_formDataRef.current)

    // If error exists then report failure.
    if (_JoiSchemaValidate.error) return setError(_JoiSchemaValidate.error?.message)

    /*
     * Update _formDataRef with routePlanId
     * if it doesn't exist and also make sure that mode
     * is not create only.
     */
    if (!isCreateOnly && _.isEmpty(_formDataRef.current?.routePlanId)) _formDataRef.current = { ..._formDataRef.current, 'routePlanId': passOn?.routePlanId }
    if (isCreateOnly && _.isEmpty(_formDataRef.current?.routeId)) _formDataRef.current = { ..._formDataRef.current, 'routeId': passOn?.routeId }

    /*
     * Update _formDataRef with input and routePlan
     * if it doesn't exist. Also make sure that mode is
     * not create only.
     */
    if (_.isEmpty(_formDataRef.current?.input)) _formDataRef.current = ({ 'input': [_formDataRef.current], ..._formDataRef.current })

    // Execute update mutation.
    const _MutationLBRRoutePlanUpdate = await [isCreateOnly ? MutationLBRRoutePlanCreate : MutationLBRRoutePlanUpdate]?.[0]({
      'variables': {
        'routePlanId': _.isEmpty(passOn?.routePlanIds) ? isCreateOnly ? '' : passOn?.routePlanId : void 0,
        'routePlanIds': _.isEmpty(passOn?.routePlanIds) ? void 0 : isCreateOnly ? '' : passOn?.routePlanIds,
        'input': _.pick(_formDataRef.current, 'input').input.map(j => {
          // Const assignment.
          let _mcc, _mnc

          // Variable assignment.
          _mcc = j?.mcc
          _mnc = j?.mnc

          if (j?.mcc && (j?.mcc?.includes('(') || j?.mcc?.includes(')'))) _mcc = j.mcc.split('(')[1].split(')')[0]
          if (j?.mnc && 0 < j?.mnc?.length) _mnc = _.compact(_.flatten(j?.mnc.map(r => r?.includes('(') && r?.includes(')') ? r.split('(')[1].split(')')[0] : r)))

          // Return data.
          return ({
            'mcc': _mcc,
            'mnc': _mnc,
            'loadBalancerConfiguration': isLoadBalancingOn ? _loadBalancerFilteredData : [],
            ...(isCreateOnly ? { 'routeId': passOn?.routeId } : {})
          })
        })
      }
    })

    // If mutation caught an exception then report failure.
    if (_MutationLBRRoutePlanUpdate instanceof Error) return _MutationLBRRoutePlanUpdate

    // If Update contains data then update routePlanIds.
    if (!_.isEmpty(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanUpdate)) _routePlanIds = _.compact(_.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanUpdate, 'id'))
    if (!_.isEmpty(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanCreate)) _routePlanIds = _.compact(_.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanCreate, 'id'))

    // Check if create was successful.
    if (!_.isEmpty(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanCreate) && _.every(_.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanCreate, 'status') ?? [], j => 'CREATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the route plan.')

      // Execute refetch.
      refetch?.()

      // On successful create close modal.
      return onClose?.()
    }
    if (!_.isEmpty(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanUpdate) && _.every(_.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanUpdate, 'status') ?? [], j => 'UPDATE_SUCCESSFUL' === j)) {
      // Style guide.
      toast('Successfully upserted the route plan.')

      // Execute refetch.
      refetch?.()

      // Report failure.
      return onClose?.()
    }

    // Style Guide.
    toast((_.isEmpty(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanCreate) ? [0 === _.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanUpdate, 'message')?.length ? 'Same plan already exists. kindly delete it or modify it instead.' : _.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanUpdate, 'message')]?.join(' ') : _.pluck(_MutationLBRRoutePlanUpdate?.data?.LBRRoutePlanCreate, 'message')?.join(' ')))

    // Report void.
    return void 0
  }

  // Event handler.
  React.useEffect(() => {
    // _Async handler.
    const _Async = async () => {
      // Const assignment.
      const _QueryLBRRoutePlanReadQuery = await QueryLBRRoutePlanRead({ 'variables': { 'routePlanId': isCreateOnly ? 'UN_KNOWN' : passOn?.routePlanId } })

      // If query caught an exception then report failure.
      if (_QueryLBRRoutePlanReadQuery instanceof Error) return _QueryLBRRoutePlanReadQuery

      /*
       * If routePlan details fetch complete then
       * update its value.
       */
      if (_QueryLBRRoutePlanReadQuery?.data?.LBRRoutePlanRead) {
        // Const assignment.
        const _routePlanRead = _.first(_QueryLBRRoutePlanReadQuery?.data?.LBRRoutePlanRead)

        // Update form data.
        _formDataRef.current = {
          'routePlanId': passOn?.routePlanId,
          'createdAt': _routePlanRead?.createdAt,
          'updatedAt': _routePlanRead?.updatedAt,
          'mcc': _routePlanRead?.Mcc?.mcc ? `${_routePlanRead?.Mcc?.countryName} (${_routePlanRead?.Mcc?.mcc})` : void 0,
          'mnc': _routePlanRead?.Mnc?.mnc ? `(${_routePlanRead?.Mnc?.mnc}) ${_routePlanRead?.Mnc?.network}` : void 0,
          'isPaused': _routePlanRead?.isPaused
        }

        /*
         * If given routePlan contains loadBalancerConfiguration
         * then turn own load balancing.
         */
        if (_routePlanRead?.loadBalancerConfiguration) {
          // Update forms.
          if (0 < _routePlanRead?.loadBalancerConfiguration?.length) {
            // Update loadBalancing.
            setIsLoadBalancingOn(true)

            /*
             * Loop over jam and push each key to _loadBalancingRef.current
             * object.
             */
            _routePlanRead?.loadBalancerConfiguration.map((i, j) => {
              // Update loadBalancingRef.
              _loadBalancingRef.current[`vendorAccountId__${j}`] = i?.vendorAccountId && i?.vendorDisplayName ? `${i?.vendorDisplayName} (${i?.vendorAccountId})` : void 0
              _loadBalancingRef.current[`percentage__${j}`] = i?.percentage

              // Return data.
              return ({
                [`vendorAccountId__${j}`]: i?.vendorAccountId && i?.vendorDisplayName ? `${i?.vendorDisplayName} (${i?.vendorAccountId})` : void 0,
                [`percentage__${j}`]: i?.percentage
              })
            })

            setForms(Array.from({ 'length': _routePlanRead?.loadBalancerConfiguration?.length }))
          } else {
            // Update forms.
            setForms([1])
            setIsLoadBalancingOn(false)
          }
        }

        // Update state.
        return setForceReRender(String.random(13))
      }

      // Report failure.
      return void 0
    }; _Async()
  }, [passOn, isOpen])

  // Const assignment.
  const _isLoading = MutationLBRRoutePlanUpdateResponse.loading || MutationLBRRoutePlanCreateResponse.loading
  const _isInputDisabled = isCreateOnly ? false : _isLoading || QueryLBRRoutePlanReadResponse.loading

  // Return component.
  return (
    <form onSubmit={_SubmitForm} className='routePlanUpsert'>
      <Flex gap='22px' flexDir='column' wrap='nowrap' w='100%'>
        <Flex width='100%' gap='22px' direction={{ 'base': 'column', 'md': 'row' }}>
          <MccMncSelector
            isRequired={true}
            disabled={_isInputDisabled}
            mccValue={_formDataRef?.current?.mcc}
            mncValue={_formDataRef?.current?.mnc}
            inValidMnc={error?.includes('mnc')}
            inValidMcc={error?.includes('mcc')}
            onChange={i => {
              // Update form data.
              _formDataRef.current = ({ ..._formDataRef.current, 'mnc': i.mnc, 'mcc': i.mcc })

              // Force re-render.
              setForceReRender(String.random(5))
            }}
          />
        </Flex>
        <Flex boxShadow='14px 17px 40px 4px rgba(112, 144, 176, 0.2)' bg='gray.100' border='1px solid' borderColor='brand.500' p='22px' borderRadius={16} flexDir='column' gap='12px'>
          <Flex flexDir='row' alignItems='center' gap='6px' color='brand.500'><HiBolt /><Text fontSize='lg' fontWeight={700} color='brand.500'>Load Balancing</Text></Flex>
          <Text fontSize='md' my='12px' w='80%'>Load Balancing will be used instead of main plan <Text as='span' color='brand.500'>Vendor Account</Text> and all failing <Text as='span' color='brand.500'>Vendor Accounts</Text> will fallback to the main plan&apos;s <Text as='span' color='brand.500'>Vendor Account</Text>.</Text>
          {
            forms?.map((___, __index) => (
              <Flex flexDir={_isCurrentViewMobile ? 'column' : 'row'} alignItems='flex-end' gap='12px'
                key={String.random(18)} my='6px' mb={__index === (forms?.length - 1) ? 0 : '6px'}>
                <Flex w='100%' gap='12px' flexDir={_isCurrentViewMobile ? 'column' : 'row'}>
                  <VendorAccountSelector
                    name={`vendorAccountId__${__index}`}
                    label='Vendor Account'
                    placeholder='e.g. "Awesome Vendor Account"'
                    value={_loadBalancingRef?.current[`vendorAccountId__${__index}`]}
                    isRequired={true}
                    error={error}
                    height='0px'
                    bg='gray.300'
                    disabled={_isInputDisabled}
                    isInvalid={error?.includes(`position ${__index}`) && error?.includes(`vendorAccountId__${__index}`)}
                    onChange={({ target }) => {
                      // Over spreading.
                      const { value } = target

                      // Update _loadBalancingRef.:
                      _loadBalancingRef.current = {
                        ..._loadBalancingRef.current,
                        [`vendorAccountId__${__index}`]: value
                      }

                      // Force re-render.
                      setForceReRender(String.random(10))
                    }}
                  />
                  <MemoizedInput
                    w='100%'
                    disabled={_isInputDisabled}
                    name={`percentage__${__index}`}
                    label='Load ( in Percentage )'
                    placeholder='e.g. "10 is 10%"'
                    type='number'
                    bg='gray.300'
                    onChange={({ target }) => {
                      // Over spreading.
                      const { value } = target

                      // Update _loadBalancingRef.
                      _loadBalancingRef.current = {
                        ..._loadBalancingRef.current,
                        [`percentage__${__index}`]: isNaN(parseFloat(value, 10)) ? 0 : parseFloat(value, 10)
                      }
                    }}
                    isInvalid={(error?.includes(`position ${__index}`) && error?.includes('percentage'))}
                    data={_loadBalancingRef.current[`percentage__${__index}`]}
                  />
                </Flex>
                <Button
                  disabled={_isInputDisabled}
                  variant='basic'
                  type='submit'
                  h='40px'
                  minH='40px'
                  position='relative'
                  bg={__index === (forms.length - 1) ? 'brand.400' : 'red.400'}
                  onClick={() => {
                    // If current button is remove button.
                    if (__index !== (forms.length - 1)) {
                      // If given field already exists then remove it.
                      if (Object.keys(_loadBalancingRef.current).includes(`vendorAccountId__${__index}`)) {
                        // Remove object from _loadBalancerFilteredData.
                        delete _loadBalancingRef.current[`vendorAccountId__${__index}`]
                        delete _loadBalancingRef.current[`percentage__${__index}`]
                      }

                      // Update forms.
                      return setForms(forms?.filter((u, i) => i !== __index))
                    }

                    // Update forms.
                    return setForms([...forms, 1])
                  }}
                  _hover={{ 'bg': __index === (forms.length - 1) ? 'brand.400' : 'danger.400' }}
                  _active={{ 'bg': __index === (forms.length - 1) ? 'brand.400' : 'danger.400' }}
                  minW='50px'>
                  {__index === (forms.length - 1) ? <HiPlusCircle size={16} /> : <HiMinusCircle size={16} />}
                </Button>
              </Flex>
            ))
          }
          {error && error?.includes('LoadBalancing:') && <Text color='red.500'>{error}</Text>}
        </Flex>
      </Flex>
      <Flex
        mt='22px'
        alignItems={_isCurrentViewMobile ? 'flex-end' : 'center'}
        justifyContent='space-between'
        flexDir='row'>
        <SubmitButton
          mt={0}
          disabled={_isInputDisabled}
          onSubmit={_SubmitForm}
          defaultText={isCreateOnly ? 'Create Load BalancerPlan' : 'Update Load BalancerPlan'}
          isLoading={_isLoading} />
      </Flex>
    </form>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  'isCreateOnly': PropTypes.bool,
  'isOpen': PropTypes.bool,
  'onClose': PropTypes.func,
  'passOn': PropTypes.object,
  'refetch': PropTypes.func
}


/*
 * REDUX
 */
const _MapStateToProps = __state => ({ 'passOn': __state.PassOn })


/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index)
